/* eslint-disable no-console */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Logo from '../includes/Logo';
import '../../themes/quill-nile.css';
import getI18nService from '../../i18n/i18n';
import { Context } from '../../store/Store';
import { CustomTypography } from '../../common/CustomTypoGraphy';
import MobileNumber from './MobileNumber';
import Passcode from './Passcode';
import userService from '../../api/UserService';
import { callFetchData } from '../../common/Fetch';
import { setStorageItem } from '../../common/Storage';
import { coaStatusEnum, statusEnum } from '../../common/metadata/DataEnums';
import { getDescendantProp, useQuery } from '../../common/Utils';
import Footer from '../includes/Footer';
import Sso from '../includes/Sso';

const i18n = getI18nService();
// const env = window['USERSERVICE_ENV'] || 'dev';

const cmd = 'login';
const portalType = 'SMS_ACCESS_CODE';

const SmsLogin = () => {
  const theme = useTheme();
  let query = useQuery();
  const navigate = useNavigate();
  const [state, dispatch] = useContext(Context);
  const [portalData, setPortalData] = useState(null);
  const [requiredInput, setRequiredInput] = useState(null);
  const [showPasscodeForm, setShowPasscodeForm] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [isAccessCodeVerified, setIsAccessCodeVerified] = useState('');
  const [counter, setCounter] = useState(0);
  const [coaInterval, setCoaInterval] = useState(null);
  const [coaStatus, setCoaStatus] = useState(coaStatusEnum.pending);
  const [sessionStatus, setSessionStatus] = useState(statusEnum.pendingApproval);
  const [phoneState, setPhoneState] = useState({
    countryCode: 'US',
    phoneNumber: null,
  });
  const [formData, setFormData] = useState({});
  const [verificationState, setVerificationState] = useState('initial');
  const [editorHtml, setEditorHtml] = useState(null);
  const [selfRegAllowed, setSelfRegAllowed] = useState(false);
  const { ui, api } = state;
  const { customization } = ui || {};
  const sessionId = query.get('sessionId');
  const essid = query.get('essid');
  const mac = query.get('mac');
  const ip = query.get('ip');
  const forward = query.get('forward');

  const getPortalData = async () => {
    // const authenticated = true;
    const api = Object.assign({}, userService.LookupPortalData, {
      data: { sessionId },
    });
    const result = await callFetchData(api);
    if (result.status === 200) {
      dispatch({
        type: 'api',
        payload: {
          name: api.name,
          response: result.resp,
        },
      });

      const { resp } = result;
      if (resp && resp.allowSSO) {
        setSelfRegAllowed(true);
      }
      setPortalData(resp);
      setRequiredInput(resp.requiredInput);
    }
  };

  useEffect(() => {
    getPortalData();
    setStorageItem('smsLoginParams', `cmd=login&sessionId=${sessionId}&essid=${essid}&mac=${mac}&ip=${ip}`);
    setStorageItem('sessionId', sessionId);
  }, []);

  useEffect(() => {
    if (!portalData) return;
    try {
      let content = portalData.tnc;
      if (typeof content === 'string' && content.startsWith('{')) {
        content = JSON.parse(portalData.tnc);
      }
      setEditorHtml(content);
    } catch (error) {
      setEditorHtml(portalData.tnc);
      console.error(error);
    }
  }, [portalData]);
  // 401- expired
  // 400 - not a valid passcode
  // 403 - limit reached/already in use

  /**
   * Submit an approval request
   */
  const runCoaCheck = async () => {
    const sessionId = query.get('sessionId');

    if (counter > 5) {
      clearInterval(coaInterval);
      // Tried several time just assume it is ok at this point.
      if (sessionStatus === statusEnum.approved) setCoaStatus(coaStatusEnum.ack);
    } else {
      const api = Object.assign({}, userService.FetchSessionStatus, {
        data: { sessionId },
      });
      console.log(`Running COA check for ${counter} time.`);
      const result = await callFetchData(api);
      if (result && result.status <= 204) {
        const { resp } = result;
        if (resp && resp.status === statusEnum.approved) {
          setSessionStatus(statusEnum.approved);
          setCoaStatus(resp.coaStatus);
          clearInterval(coaInterval);
        } else if (resp && [statusEnum.pendingApproval, statusEnum.init].includes(resp.status)) {
          setSessionStatus(statusEnum.pendingApproval);
          setCoaStatus(resp.coaStatus); //resp.coaStatus
        } else {
          setSessionStatus(statusEnum.pendingApproval);
          setCoaStatus(resp.coaStatus);
        }
      } else {
        setSessionStatus(statusEnum.pendingApproval);
        setCoaStatus(coaStatusEnum.error);
        clearInterval(coaInterval);
      }
    }
  };

  useEffect(() => {
    if (sessionStatus === statusEnum.approved && coaStatus === coaStatusEnum.ack) {
      setVerificationState('verified');
      setTimeout(() => {
        navigate(`/smsloginSuccess`);
      }, 1000);
    }
  }, [sessionStatus, coaStatus]);

  useEffect(() => {
    if (counter > 0) {
      runCoaCheck();
    }
  }, [counter]);

  const handleAccessCode = async event => {
    const authenticated = true;
    setIsAccessCodeVerified('');
    const api = Object.assign({}, userService.UserLogin, {
      data: { sessionId, essid },
      params: { cmd, essid, mac }, // cmd, mac
      body: { portalType, essid, mac, accessCode }, // authenticated,
    });

    const result = await callFetchData(api);

    if (result?.status <= 204) {
      setIsAccessCodeVerified('success');
      window.location.href = '/smsloginSuccess';
      const coaInt = setInterval(() => {
        setCounter(cnt => cnt + 1);
      }, 2000);
      setCoaInterval(coaInt);
    } else if ([500, 400].includes(result?.status)) {
      setIsAccessCodeVerified('failed');
      setVerificationState('initial');
      return;
    } else if (result?.status === 401) {
      // navigate(`/passcodeExpire?cmd=login&sessionId=${sessionId}&essid=${essid}&mac=${mac}&ip=${ip}`);
      navigate(`/passcodeExpire`);
    } else if (result?.status === 403) {
      navigate(`/usedPasscode?maxDevices=${getDescendantProp(portalData, 'smsAccessCodeConfig.maxDevices')}`);
    }
  };

  useEffect(() => {
    if (accessCode) {
      handleAccessCode();
    }
  }, [accessCode]);

  const handleMobileNumSubmit = async data => {
    if (data) setFormData(data);
    const { phoneNumber, countryCode, name, email } = data || formData;
    const cmd = 'login';

    const api = Object.assign({}, userService.GenerateAccessCode, {
      data: {
        sessionId,
      },
      params: {
        cmd,
        macaddr: mac,
        essid: essid,
      },
      body: {
        type: 'SMS_ACCESS_CODE',
        guestPhoneNum: `${countryCode?.dialCode} ${phoneNumber}`,
        guestFirstName: name,
        user_mac: mac,
        guestEmail: email,
      },
    });
    const result = await callFetchData(api);
    if (result.status === 200) {
      setShowPasscodeForm(true);
    }
    // setShowPasscodeForm(true);
  };

  return (
    <>
      {customization && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            boxShadow: 'rgb(0 0 0 / 25%) 2px 4px 10px 4px',
            backgroundColor: theme.palette.background.black80 || '#000000',
            position: 'fixed',
            top: '45%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {portalData && (
            <Box
              id="sms-login-box"
              display="flex"
              flexDirection="column"
              alignContent="center"
              alignItems={{ xs: 'left', sm: 'center' }}
              justifyContent="center"
              sx={{
                px: { xs: 2, sm: 4 },
                py: 2,
                width: { xs: 400, sm: 652 },
              }}
            >
              <Box textAlign="center">
                {customization?.logo?.data && <Logo data={customization.logo.data} css={customization.logo.css} />}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignContent: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CustomTypography
                  sx={{
                    fontSize: {
                      xs: '24px',
                      sm: '36px',
                    },
                    textAlign: 'center',
                    padding: '15px 0px 4px 0px',
                  }}
                >
                  {i18n.t('Messages.secureGuestLogin')}
                </CustomTypography>
              </Box>
              <CustomTypography
                sx={{
                  fontSize: {
                    xs: '18px',
                    sm: '24px',
                  },
                  fontWeight: 500,
                  color: 'text.hint',
                  textAlign: 'center',
                }}
              >
                {i18n.t('Messages.secureAndReliableNetwork')}
              </CustomTypography>
              {isAccessCodeVerified === 'failed' && (
                <CustomTypography
                  sx={{
                    fontSize: {
                      xs: '16px',
                      sm: '24px',
                    },
                    fontWeight: 500,
                    color: 'error.main',
                    textAlign: 'center',
                  }}
                >
                  {i18n.t('Messages.invalidCode')}
                </CustomTypography>
              )}

              {showPasscodeForm ? (
                <Passcode
                  setAccessCode={setAccessCode}
                  phoneState={phoneState}
                  reSendAccessCode={handleMobileNumSubmit}
                  setShowPasscodeForm={setShowPasscodeForm}
                  verificationStatus={{ verificationState, setVerificationState }}
                />
              ) : (
                <Box>
                  <MobileNumber
                    requiredInput={requiredInput}
                    onSubmit={handleMobileNumSubmit}
                    setShowPasscodeForm={setShowPasscodeForm}
                    setPhoneNumber={{ phoneState, setPhoneState }}
                    editorHtml={editorHtml}
                  />
                  {selfRegAllowed && <Sso />}
                </Box>
              )}
            </Box>
          )}
          {!portalData && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <span>Loading...</span>
            </Box>
          )}
          <Footer />
        </Paper>
      )}
    </>
  );
};

SmsLogin.propTypes = {};

export default SmsLogin;
